import { useState, useRef } from 'react';
import { DotsSixVertical } from '@phosphor-icons/react';

/**
 * Reusable drag-and-drop wrapper for ordered lists.
 * Usage:
 *   <DraggableList items={items} onReorder={setItems} renderItem={(item, idx, dragHandleProps) => ...} />
 * 
 * Props:
 *   items: array of items
 *   onReorder: (newItems) => void — called after drop
 *   renderItem: (item, idx, dragHandleProps) => ReactNode
 *   keyExtractor: (item, idx) => string (default: idx)
 *   className: container className
 *   itemClassName: per-item className
 *   dropTargetClassName: className when item is being dragged over
 *   group: string — group name to allow cross-list drops (optional)
 *   onReceive: (item, fromGroup) => void — called when item dropped from another group
 */
export function DraggableList({
  items, onReorder, renderItem, keyExtractor,
  className = 'space-y-1.5', itemClassName = '',
  dropTargetClassName = 'ring-2 ring-brand-green/40 bg-brand-green/5',
  group, onReceive,
}) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const dragData = useRef(null);

  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    dragData.current = { group, idx, item: items[idx] };
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ group, idx }));
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverIdx(idx);
  };

  const handleDragLeave = () => { setOverIdx(null); };

  const handleDrop = (e, targetIdx) => {
    e.preventDefault();
    setOverIdx(null);
    let sourceData;
    try { sourceData = JSON.parse(e.dataTransfer.getData('text/plain')); } catch { sourceData = dragData.current; }
    if (!sourceData) { setDragIdx(null); return; }

    // Cross-group drop
    if (group && sourceData.group && sourceData.group !== group && onReceive) {
      onReceive(sourceData.item, sourceData.group, targetIdx);
      setDragIdx(null);
      return;
    }

    // Same-list reorder
    const srcIdx = sourceData.idx ?? dragIdx;
    if (srcIdx === null || srcIdx === targetIdx) { setDragIdx(null); return; }
    const arr = [...items];
    const [moved] = arr.splice(srcIdx, 1);
    arr.splice(targetIdx > srcIdx ? targetIdx - 1 : targetIdx, 0, moved);
    onReorder(arr);
    setDragIdx(null);
  };

  const handleDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  const dragHandleProps = {
    className: 'cursor-grab active:cursor-grabbing text-brand-placeholder hover:text-brand-green flex-shrink-0',
    children: <DotsSixVertical size={14} />,
  };

  return (
    <div className={className}>
      {items.map((item, idx) => {
        const key = keyExtractor ? keyExtractor(item, idx) : idx;
        const isDragging = dragIdx === idx;
        const isOver = overIdx === idx && dragIdx !== idx;
        return (
          <div
            key={key}
            draggable
            onDragStart={e => handleDragStart(e, idx)}
            onDragOver={e => handleDragOver(e, idx)}
            onDragLeave={handleDragLeave}
            onDrop={e => handleDrop(e, idx)}
            onDragEnd={handleDragEnd}
            className={`${itemClassName} ${isDragging ? 'opacity-40' : ''} ${isOver ? dropTargetClassName : ''} transition-all`}
            data-testid={`draggable-item-${idx}`}
          >
            {renderItem(item, idx, dragHandleProps)}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Inline editable text field.
 * Shows text normally, click to edit, saves on blur/Enter.
 */
export function InlineEdit({ value, onSave, className = 'text-sm text-brand-text', inputClassName = '', placeholder = '', tag: Tag = 'span', testId }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);

  const save = () => {
    if (val.trim() !== value) onSave(val.trim());
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        value={val} onChange={e => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setVal(value); setEditing(false); } }}
        className={`bg-transparent border-b-2 border-brand-green outline-none w-full ${inputClassName || className}`}
        autoFocus data-testid={testId ? `${testId}-input` : undefined}
      />
    );
  }
  return (
    <Tag
      className={`cursor-pointer group ${className}`}
      onClick={() => { setVal(value); setEditing(true); }}
      data-testid={testId}
    >
      {value || <span className="text-brand-placeholder italic">{placeholder || 'Clica para editar'}</span>}
    </Tag>
  );
}
